
import { useState } from 'react';
import { useToast } from '@/components/ui/use-toast';
import { 
  fetchEmployees, 
  removeEmployee,
  toggleEmployeeActiveStatus
} from '@/services/admin';

export const useEmployeeManagement = () => {
  const { toast } = useToast();
  const [employees, setEmployees] = useState<any[]>([]);
  const [isAddEmployeeOpen, setIsAddEmployeeOpen] = useState<boolean>(false);
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const [filterStatus, setFilterStatus] = useState<string>("all");

  const filteredEmployees = employees.filter(emp => {
    if (filterStatus === "all") return true;
    if (filterStatus === "active") return emp.is_active;
    if (filterStatus === "inactive") return !emp.is_active;
    return true;
  });

  const loadEmployees = async () => {
    const { data, error } = await fetchEmployees();
    
    if (error) {
      toast({
        title: "Error",
        description: "Failed to load employees.",
        variant: "destructive",
      });
    }
    
    setEmployees(data);
  };

  const handleRemoveEmployee = async (employeeId: string) => {
    if (!employeeId) return;

    if (window.confirm('Are you sure you want to remove this employee? This action cannot be undone.')) {
      setIsProcessing(true);
      
      try {
        const { success, error } = await removeEmployee(employeeId);

        if (!success) throw error;

        // Update local state immediately
        setEmployees(prev => prev.filter(emp => emp.id !== employeeId));
        
        // Show success message
        toast({
          title: "Employee Removed",
          description: `Employee has been removed from the system.`,
        });
        
        // Refresh the list
        await loadEmployees();
        
      } catch (error) {
        console.error('Error removing employee:', error);
        toast({
          title: "Error",
          description: "Failed to remove employee.",
          variant: "destructive",
        });
      } finally {
        setIsProcessing(false);
      }
    }
  };

  const handleToggleEmployeeActive = async (employeeId: string, isActive: boolean) => {
    setIsProcessing(true);
    
    try {
      const { success, updatedEmployee, error } = await toggleEmployeeActiveStatus(employeeId, isActive);
      
      if (!success) throw error;
      
      // Update local state
      setEmployees(prev => 
        prev.map(emp => emp.id === employeeId ? { ...emp, is_active: isActive } : emp)
      );
      
      toast({
        title: isActive ? "Employee Activated" : "Employee Deactivated",
        description: `Employee has been ${isActive ? 'activated' : 'deactivated'}.`,
      });
      
    } catch (error: any) {
      console.error('Error toggling employee status:', error);
      toast({
        title: "Error",
        description: `Failed to update employee status: ${error.message || 'Unknown error'}`,
        variant: "destructive",
      });
    } finally {
      setIsProcessing(false);
    }
  };

  return {
    employees: filteredEmployees,
    isAddEmployeeOpen,
    setIsAddEmployeeOpen,
    isProcessing,
    filterStatus,
    setFilterStatus,
    loadEmployees,
    handleRemoveEmployee,
    handleToggleEmployeeActive
  };
};
